import create from 'zustand';

import {
  BondCouponHistory,
  BondPortfolio,
  BondPortfolioProduct,
  BondPortfolioWithProduct,
  BondTransaction,
} from 'global/Securities/types/api/bond';
import {
  getBondPortfolioDetail,
  getBondPortfolioList,
} from 'global/Securities/api/bond';
import transformData from 'utils/securities/transformData';
import { PortfolioStateItem } from 'global/Securities/types/store/portfolio';

interface State {
  detailBySymbol: BondPortfolio;
  details: BondPortfolio;
  portfolios: BondPortfolioWithProduct[];
  bondPortfolioState: PortfolioStateItem[];
  transactions: BondTransaction[];
  product?: BondPortfolioProduct;
  couponHistories: BondCouponHistory;
  isLoading: boolean;
  isLoadingList: boolean;
}

interface Actions {
  loadPortfolioDetails(
    id: string,
    withCouponHistories?: boolean,
  ): Promise<void>;
  loadPortfolioList(): Promise<void>;
  clear(): void;
  clearPortfolioList(): void;
  clearAssets(): void;
}

type Store = State & Actions;

const initialState: State = {
  detailBySymbol: undefined,
  details: undefined,
  portfolios: [],
  bondPortfolioState: [],
  transactions: [],
  product: undefined,
  couponHistories: undefined,
  isLoading: false,
  isLoadingList: false,
};

export const useBondPortfolioStore = create<Store>((set) => ({
  ...initialState,

  async loadPortfolioList() {
    try {
      set({ isLoadingList: true });
      const response = await getBondPortfolioList();

      const { data } = response.data;

      const { portfolios, ...rest } = data;

      set({
        portfolios,
        bondPortfolioState: portfolios.map(
          transformData.portfolioItemFromBonds,
        ),
        details: rest,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        portfolios: initialState.portfolios,
        details: initialState.details,
        bondPortfolioState: initialState.bondPortfolioState,
      });
      Promise.reject(error);
    }
  },

  async loadPortfolioDetails(id, withCouponHistories = false) {
    try {
      set({ isLoading: true });

      const response = await getBondPortfolioDetail(id, withCouponHistories);

      const { data } = response.data;
      const { transactions, coupon_histories, product, ...rest } = data;

      set({
        transactions,
        product,
        couponHistories: coupon_histories,
        detailBySymbol: rest,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      Promise.reject(error);
    }
  },

  clear() {
    set(initialState);
  },

  clearPortfolioList() {
    set({
      portfolios: initialState.portfolios,
      transactions: initialState.transactions,
      product: initialState.product,
    });
  },

  clearAssets() {
    set({
      transactions: initialState.transactions,
      detailBySymbol: initialState.detailBySymbol,
    });
  },
}));
